/**
 * Minimal hover/focus tooltip primitive. No external dependency —
 * the app's other primitives (Tabs, Dialog, etc.) are hand-rolled in
 * the same style, so adding `@radix-ui/react-tooltip` for one
 * use-site (chart-title (i) icons on the Analytics page) isn't
 * worth the dep weight.
 *
 * Accessibility:
 *   - The trigger renders as a real `<button>`, so the (i) icon
 *     is keyboard-focusable and the hover-vs-focus distinction is
 *     real for keyboard users. `children` becomes the visible label
 *     inside the button (typically the lucide `Info` icon).
 *   - The popover gets a stable `id` (via `useId()`); when open, the
 *     button gets `aria-describedby` pointing at that id, so screen
 *     readers announce the tooltip content on focus.
 *   - `role="tooltip"` on the popover.
 *   - Default `side='bottom'` so the popover drops below the
 *     trigger and the chart title stays unobscured.
 *
 * Scope deliberately tight: no `delayDuration` knob, no portal, no
 * smart positioning (flip-when-clipped). Good enough for "info icon
 * next to a chart title"; promote to a richer primitive when a
 * second use site demands it.
 */
import { useId, useState, useRef, useLayoutEffect, type ReactNode } from 'react'
import { cn } from '@/lib/utils'

interface TooltipProps {
  /** Tooltip content. Plain text or a rich ReactNode. */
  content: ReactNode
  /** The trigger contents — typically the `(i)` icon. Rendered inside
   * a `<button>` for keyboard focus, so the icon itself does NOT
   * need to be tabbable. */
  children: ReactNode
  /** Which side of the trigger to render on. Default `bottom` — drops
   * below the trigger so the (i) icon stays clearly visible above
   * its explanation and the chart title isn't obscured. */
  side?: 'top' | 'bottom'
  /** Extra classes on the popover. */
  className?: string
  /** Accessible label for the button when the icon alone doesn't
   * convey purpose. Defaults to `'More info'`. */
  triggerLabel?: string
  /** Horizontal placement of the popover relative to the trigger.
   * `center` (default) centres it. `start` aligns the popover's
   * left edge to the trigger — use it for a trigger near a
   * container's left edge so a wide popover opens inward instead
   * of off the edge (where an `overflow` ancestor clips it). `end`
   * mirrors that on the right. No automatic flip-when-clipped — the
   * caller picks the safe side. */
  align?: 'center' | 'start' | 'end'
}

export function Tooltip({
  content,
  children,
  side = 'bottom',
  align = 'center',
  className,
  triggerLabel = 'More info',
}: TooltipProps) {
  const id = useId()
  const [open, setOpen] = useState(false)
  // Horizontal collision clamp. The popover anchors to a fixed side
  // (`align`), and there is no flip-when-clipped — so a popover near a
  // viewport (or clipping container) edge gets cut off. After it opens we
  // measure it and shift it horizontally just enough to stay on screen.
  // The shift is 0 whenever it already fits, so this never moves a
  // tooltip that wasn't clipping. Replaces the per-call "pick the safe
  // side" guesswork that broke when a card changed columns responsively
  // (#1614 follow-up). Runs in a layout effect so the shift lands before
  // paint — no visible jump.
  const popRef = useRef<HTMLSpanElement>(null)
  const [shiftX, setShiftX] = useState(0)
  useLayoutEffect(() => {
    if (!open) {
      // Reset so the next open re-measures from the natural position.
      if (shiftX !== 0) setShiftX(0)
      return
    }
    const el = popRef.current
    if (!el) return
    const margin = 8
    const rect = el.getBoundingClientRect()
    // width 0 = not laid out (e.g. jsdom in tests) — nothing to measure.
    if (rect.width === 0) return
    // Clamp within the nearest CLIPPING ancestor (a dialog body with
    // overflow:auto, a scroll area, …), not just the viewport. A centered
    // dialog narrower than the viewport would otherwise let the popover
    // spill past the dialog's edge — still inside the viewport, so the
    // viewport-only clamp never moved it — and get cut off by the dialog's
    // overflow. Falls back to the viewport when there is no clipping
    // ancestor (e.g. a chart-title tooltip on an open page).
    let leftBound = margin
    let rightBound = window.innerWidth - margin
    for (let node = el.parentElement; node; node = node.parentElement) {
      const o = getComputedStyle(node)
      if (o.overflowX !== 'visible' || o.overflowY !== 'visible') {
        const cr = node.getBoundingClientRect()
        leftBound = Math.max(leftBound, cr.left + margin)
        rightBound = Math.min(rightBound, cr.right - margin)
        break
      }
    }
    // Wider than the available band → clamping can't win; leave it
    // anchored rather than oscillate between the two clipped edges.
    if (rect.width >= rightBound - leftBound) {
      if (shiftX !== 0) setShiftX(0)
      return
    }
    // `rect` already includes the current shift, so adjust relative to it.
    let next = shiftX
    if (rect.left < leftBound) next = shiftX + (leftBound - rect.left)
    else if (rect.right > rightBound) next = shiftX - (rect.right - rightBound)
    if (next !== shiftX) setShiftX(next)
  }, [open, shiftX])

  // Center alignment carries its own -50% base; fold the clamp shift into
  // one transform so the two don't fight (a className `-translate-x-1/2`
  // would be overridden by an inline `transform`).
  const transform =
    align === 'center'
      ? `translateX(calc(-50% + ${shiftX}px))`
      : `translateX(${shiftX}px)`
  return (
    <span className="relative inline-flex items-center">
      <button
        type="button"
        aria-label={triggerLabel}
        aria-describedby={open ? id : undefined}
        // No background / border on the button — the visible affordance
        // is the icon inside. `focus-visible` keeps a focus ring only
        // for keyboard navigation, not mouse clicks.
        className="inline-flex items-center rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        {children}
      </button>
      {open && (
        <span
          ref={popRef}
          id={id}
          role="tooltip"
          style={{ transform }}
          // `max-w-md` (28rem) makes the popover wide rather than tall —
          // long lines wrap less aggressively, which is easier to scan
          // than a narrow vertical column of text.
          className={cn(
            'absolute z-50 px-3 py-2 rounded-md bg-popover text-popover-foreground text-xs leading-relaxed shadow-md border w-max max-w-md whitespace-normal pointer-events-none',
            side === 'top' ? 'bottom-full mb-1.5' : 'top-full mt-1.5',
            // The center base offset moves into the inline transform above;
            // here we only set the anchor edge.
            align === 'start' ? 'left-0' : align === 'end' ? 'right-0' : 'left-1/2',
            className,
          )}
        >
          {content}
        </span>
      )}
    </span>
  )
}
